<template>
  <div class="container" v-observe-visibility="observeVisibility">
    <FormSection
      v-if="visible"
      :title="title"
      :lazy="false"
      v-bind:class="{
        modified: inputModified,
        'save-failed': needsFailedStyling,
        'save-success': saveSuccessful && !inputModified,
      }"
      class="vld-parent"
    >
      <Icon
        :src="getReviewStatusIcon(reviewStatusId)"
        class="review-status-icon"
        v-tooltip.bottom="getReviewStatusTooltip(reviewStatusId)"
      />
      <AttachmentDialog
        v-if="showAttachmentDialog"
        :parentRecordId="parentRecordId"
        @close="onAttachmentDialogClose"
        @fileListUpdated="onFileListUpdated"
      />
      <AdditionalInformationDialog
        v-if="showInformationDialog"
        :item="item"
        :currentInputValue="$refs.input.inputValueToCompare"
        :parentId="parentRecordId"
        @close="onAdditionalInformationDialogClose"
        @commentsUpdated="onCommentsUpdated"
        @outstandingTileCommentsAdded="onOutstandingTileCommentsAdded"
        :outstandingComments="commentsData"
        :previouslySaved="previouslySaved"
        :inputComponentName="getInputComponentName(item)"
        :contextValidationMessage="tileValidationMessage"
      />
      <Loading :active="isLoading" :canCancel="false" :isFullPage="false" loader="bars" />
      <FormField :name="item.id" :label="item.sustainabilityMetric" ref="formfield">
        <Component
          :is="getInputComponentName(item)"
          :item="item"
          ref="input"
          :isReadOnlyDialog="isReadOnlyDialog"
          :previouslySaved="previouslySaved"
          :contextValidationMessage="tileValidationMessage"
        ></Component>
        <Flex justify-between py-1>
          <Flex dialog-controls justify-start>
            <Button
              class="attachment-button"
              v-if="!isCalculatedOrFormulaMetric || item.resultExists"
              :disabled="isAttachmentButtonDisabled"
              :outlined="true"
              :color="item.attachmentsExists && !isAttachmentButtonDisabled ? 'primary' : ''"
              :appendIcon="item.attachmentsExists ? 'bwise/doc_filled' : 'bwise/doc'"
              v-tooltip.bottom="$t('Button.attachments')"
              @click="onAttachmentIconClick"
            ></Button>
            <Button
              v-if="!isCalculatedOrFormulaMetric || item.resultExists"
              :outlined="true"
              :color="item.commentsExists ? 'primary' : ''"
              :appendIcon="item.commentsExists ? 'bwise/alert_info' : 'bwise/info'"
              v-tooltip.bottom="$t('Button.additionalinformation')"
              @click="onInformationIconClick"
            ></Button>
          </Flex>
          <Flex justify-end>
            <template v-if="isCalculatedOrFormulaMetric">
              <Button
                class="calc-button"
                v-if="item.resultExists"
                :outlined="true"
                appendIcon="bwise-full/calculator"
                v-tooltip.bottom="$t('Button.calc')"
                @click="onCalcButtonClick"
              ></Button>
            </template>
            <template v-else>
              <Button
                class="save-button"
                :disabled="isReadOnlyDialog || isEmptyInput || shouldDisableSave"
                :outlined="true"
                appendIcon="bwise-full/save"
                v-tooltip.bottom="$t('Button.Save')"
                @click="onDialogSaveClick"
              ></Button>
            </template>
            <template v-if="!item.resultExists">
              <span class="metric-type-label" v-if="item.calculatedMetric">{{
                $t('calculatedMetric')
              }}</span>
              <span class="metric-type-label" v-if="item.formulaMetric">{{
                $t('formulaMetric')
              }}</span>
            </template>
          </Flex>
        </Flex>
      </FormField>
    </FormSection>
  </div>
</template>

<script>
import FormField from '@wizer/core/components/form/FormField'
import FormSection from '@wizer/core/components/form/FormSection'
import Flex from '@wizer/core/components/flex/Flex'
import Button from '@wizer/core/components/button/Button'
import Icon from '@wizer/core/components/icon/Icon'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { mapActions, mapState } from 'vuex'
import events from '@wizer/core/events'
import SustainabilityDataService from '@/services/SustainabilityDataService'
import SustainabilityConstants from '@/components/sustainability/SustainabilityConstants'
import SustainabilityReviewStatus from '@/components/sustainability/SustainabilityReviewStatus'
import AttachmentDialog from '@/components/sustainability/AttachmentDialog'
import AdditionalInformationDialog from '@/components/sustainability/AdditionalInformationDialog'
import SustainabilityContextValidatorMixin from '@/mixins/SustainabilityContextValidatorMixin'
import UsingColorPalette from '@/mixins/UsingColorPalette'
import TrackVisibility from '@/mixins/TrackVisibility'
import TextInput from './inputs/TextInput'
import IntegerInput from './inputs/IntegerInput'
import ConvertibleDecimalInput from './inputs/ConvertibleDecimalInput'
import DecimalInput from './inputs/DecimalInput'
import RadioInput from './inputs/RadioInput'

export default {
  name: 'DataEntryDialog',
  components: {
    AdditionalInformationDialog,
    AttachmentDialog,
    FormField,
    FormSection,
    Icon,
    TextInput,
    IntegerInput,
    ConvertibleDecimalInput,
    DecimalInput,
    RadioInput,
    Button,
    Flex,
    Loading,
  },
  props: {
    item: Object,
    title: String,
    group: String,
  },
  mixins: [SustainabilityContextValidatorMixin, UsingColorPalette, TrackVisibility],
  data() {
    return {
      value: null,
      saveFailed: false,
      saveSuccessful: false,
      isLoading: false,
      inputModified: false,
      isUpdatingCalculatedValue: false,
      inputContainsValidationError: false,
      showAttachmentDialog: false,
      showInformationDialog: false,
      parentRecordId: null,
      previouslySaved: this.item.resultExists,
      commentsData: [],
      tileValidationMessage: '',
      reviewStatusId: this.item.reviewStatusId,
    }
  },
  beforeDestroy() {
    events.unsubscribe('save-all', this.onSaveAll)
    events.unsubscribe('re-calculate', this.calculate)
  },
  async mounted() {
    events.subscribe('save-all', this.onSaveAll)
    if (this.item.calculatedMetric || this.item.formulaMetric) {
      events.subscribe('re-calculate', this.calculate)
    }
    this.parentRecordId = this.item.id
    events.subscribe('scroll-to-item', this.scrollTo)
  },
  computed: {
    isAttachmentButtonDisabled() {
      return (!this.item.resultExists && !this.saveSuccessful) || this.item.readOnly
    },
    needsFailedStyling() {
      const failedDueToContextValidation = this.inputModified && this.tileValidationMessage
      return (
        this.saveFailed &&
        (!this.inputModified || this.savedInputWasRemoved || failedDueToContextValidation)
      )
    },
    isEmptyInput() {
      return !this.currentInputValue.inputValue
    },
    isReadOnlyDialog() {
      return this.item.calculatedMetric || this.item.formulaMetric || this.item.readOnly
    },
    isCalculatedOrFormulaMetric() {
      return this.item.calculatedMetric || this.item.formulaMetric
    },
    shouldDisableSave() {
      // Context validation errors should not disable the ability to safe, as we want to compare
      // to the latest data which we get on save click
      const shouldPreventDisabling =
        this.failedContextValidations.length &&
        this.failedContextValidations.indexOf(this.item.id) > -1
      const shouldBeDisabled =
        !this.inputModified || (this.inputModified && this.inputContainsValidationError)
      if (shouldPreventDisabling) {
        return false
      }
      return shouldBeDisabled
    },
    savedInputWasRemoved() {
      return this.previouslySaved && this.isEmptyInput && this.inputModified
    },
    currentInputValue() {
      // This is to accommodate the Amount field which stores it's values as 'amount' property
      if (this.$refs.input?.inputRequiresInitialAdjustment()) {
        return this.$refs.input.inputValueToPost
      }
      return {
        inputValue: this.formValues[this.item.id],
      }
    },
    ...mapState({
      formValues: (state) => state.SustainabilityData.formValues,
      failedContextValidations: (state) => state.SustainabilityData.failedContextValidations,
    }),
  },

  methods: {
    scrollTo(itemId) {
      if (itemId === this.item.id) {
        this.$el.scrollIntoView()
      }
    },
    onSaveAll() {
      if (this.inputModified) {
        const saveToBeExecuted = async () => {
          if (this.savedInputWasRemoved) {
            this.saveFailed = true
          } else if (!this.isEmptyInput) {
            await this.onDialogSaveClick({ isSaveAllOriginated: true })
          }
        }
        this.addSaveToExecuteInSequence({ id: this.item.id, saveToBeExecuted })
      }
    },
    onFileListUpdated() {
      this.item.attachmentsExists = true
    },
    onCommentsUpdated() {
      // Only mark as modified on adding an initial comment, so that the save button is re-enabled
      // and we could execute the validations again
      if (!this.item.commentsExists) {
        this.addModifiedRecord(this.item.id)
      }
      this.inputModified = true
      this.item.commentsExists = true
      this.executeContextValidation()
    },
    onOutstandingTileCommentsAdded(comments) {
      this.commentsData = comments
      this.onCommentsUpdated()
    },
    onAttachmentDialogClose() {
      this.showAttachmentDialog = false
    },
    onAdditionalInformationDialogClose() {
      this.showInformationDialog = false
    },
    getFormDataForSubmit() {
      return {
        sustainabilityMetric: this.item.sustainabilityMetricId,
        metricInputType: this.item.sustainabilityMetricInputType,
        timePeriod: this.item.timePeriodId,
        location: this.item.locationId,
        inputValue: this.currentInputValue,
        commentsData: this.commentsData,
      }
    },
    async calculate(ids) {
      if (ids?.includes(this.item.id) && (this.item.calculatedMetric || this.item.formulaMetric)) {
        await this.onCalcButtonClick()
      }
    },
    async onCalcButtonClick() {
      let data
      let results
      this.saveSuccessful = false
      this.saveFailed = false
      this.isLoading = true
      try {
        if (this.item.formulaMetric) {
          data = this.getFormDataForSubmit()
          data.formulaMetric = this.item.formulaMetric
          results = (await SustainabilityDataService.calculateFormula(data)).data
        } else {
          data = { resultId: this.item.id }
          results = (await SustainabilityDataService.calculate(data)).data
        }
        if (results.error) {
          this.saveFailed = true
          await this.addFailedSave()
        } else {
          this.isUpdatingCalculatedValue = true
          await this.$refs.input.updateValue(results.inputValue)
          this.isUpdatingCalculatedValue = false
          this.saveSuccessful = true
        }
      } catch (error) {
        this.saveFailed = true
        await this.addFailedSave()
      }
      this.isLoading = false
    },

    async onDialogSaveClick(event) {
      // Prepare tile for saving
      this.saveSuccessful = false
      this.saveFailed = false
      this.isLoading = true

      if (!this.item.commentsExists) {
        // Do the context validations including any required async requests
        const containsContextValidationError = await this.executeContextValidation()
        if (containsContextValidationError) {
          this.saveFailed = true
          await this.handleContextValidationError(event.isSaveAllOriginated)
          await this.incrementSaveNumber()
          return
        }
      }

      this.inputModified = false
      // Execute data save
      try {
        const data = this.getFormDataForSubmit()
        const saveData = (await SustainabilityDataService.save(data)).data
        this.parentRecordId = saveData.resultId
        this.saveSuccessful = true
        this.reviewStatusId = saveData.reviewStatusId
        if (!this.previouslySaved) {
          this.newRecordSaved(this.group)
          this.previouslySaved = true
        }
        if (this.item.linkedMetrics?.length) {
          events.publish('re-calculate', this.item.linkedMetrics)
        }
        if (!event.isSaveAllOriginated) {
          this.removeModifiedRecord(this.item.id)
        }
      } catch (error) {
        this.saveFailed = true
        this.saveAllOriginatedAction(event, () => this.addFailedSave())

        // await this.addFailedSave()
      } finally {
        this.saveAllOriginatedAction(event, () => this.incrementSaveNumber())
        // await this.incrementSaveNumber()
      }
      this.isLoading = false
    },
    saveAllOriginatedAction(event, action) {
      if (event.isSaveAllOriginated) {
        action()
      }
    },
    getInputComponentName(item) {
      if (item.sustainabilityMetricInputType === SustainabilityConstants.BOOLEAN_INPUT_TYPE_ID) {
        return 'RadioInput'
      }
      if (item.sustainabilityMetricInputType === SustainabilityConstants.TEXT_INPUT_TYPE_ID) {
        return 'TextInput'
      }
      if (item.sustainabilityMetricInputType === SustainabilityConstants.INTEGER_INPUT_TYPE_ID) {
        return 'IntegerInput'
      }
      if (
        item.sustainabilityMetricInputType === SustainabilityConstants.DECIMAL_INPUT_TYPE_ID &&
        item.inputUnitId
      ) {
        return 'ConvertibleDecimalInput'
      }
      if (item.sustainabilityMetricInputType === SustainabilityConstants.DECIMAL_INPUT_TYPE_ID) {
        return 'DecimalInput'
      }
      // No supported input component found
      return ''
    },
    getReviewStatusIcon(reviewStatusId) {
      if (reviewStatusId === SustainabilityReviewStatus.IN_REVIEW) {
        return 'bwise-full/search'
      }
      if (reviewStatusId === SustainabilityReviewStatus.REJECTED) {
        return 'bwise/workflow_declined'
      }
      if (reviewStatusId === SustainabilityReviewStatus.APPROVED) {
        return 'bwise/workflow_approved'
      }
      return ''
    },
    getReviewStatusTooltip(reviewStatusId) {
      if (reviewStatusId === SustainabilityReviewStatus.IN_REVIEW) {
        return this.$t('ReviewStatus.in_review')
      }
      if (reviewStatusId === SustainabilityReviewStatus.REJECTED) {
        return this.$t('ReviewStatus.rejected')
      }
      if (reviewStatusId === SustainabilityReviewStatus.APPROVED) {
        return this.$t('ReviewStatus.approved')
      }
      return ''
    },
    onAttachmentIconClick() {
      this.setCurrentResultId(this.parentRecordId)
      this.showAttachmentDialog = true
    },
    onInformationIconClick() {
      this.showInformationDialog = true
    },
    ...mapActions({
      setCurrentResultId: 'SustainabilityData/setCurrentResultId',
      addModifiedRecord: 'SustainabilityData/addModifiedRecord',
      removeModifiedRecord: 'SustainabilityData/removeModifiedRecord',
      newRecordSaved: 'SustainabilityData/newRecordSaved',
      addFailedSave: 'SustainabilityData/addFailedSave',
      incrementSaveNumber: 'SustainabilityData/incrementSaveNumber',
      addSaveToExecuteInSequence: 'SustainabilityData/addSaveToExecuteInSequence',
      addFailedContextValidation: 'SustainabilityData/addFailedContextValidation',
      removeFailedContextValidation: 'SustainabilityData/removeFailedContextValidation',
    }),
  },
  watch: {
    currentInputValue(newValue, oldValue) {
      if (this.$refs.input?.inputRequiresInitialAdjustment() && !oldValue.adjusted) {
        // Prevents adjusted fields from being marked as modified on initial load
        return
      }
      if (!!newValue && !this.inputModified && !this.isUpdatingCalculatedValue) {
        this.inputModified = true
        this.addModifiedRecord(this.item.id)
      }
      // Check for context validations only if save was executed unsuccessfully
      if (this.saveFailed) {
        this.executeContextValidation()
      }
      this.$nextTick(() => {
        this.inputContainsValidationError =
          this.$refs.formfield && !!this.$refs.formfield.inputError
      })
    },
  },
}
</script>

<style scoped lang="postcss">
.container {
  @apply w-full mr-8;
  min-height: 16rem;
  scroll-margin: 4rem;
}
.modified {
  @apply bg-yellow-100;
}
.save-success {
  @apply bg-green-100;
}
.save-failed {
  @apply bg-red-100;
}
.metric-type-label {
  @apply flex flex-wrap content-center font-bold text-blue-700;
}
.attachment-button {
  @apply mr-1;
}
.review-status-icon {
  @apply absolute font-bold;
  top: 20px;
  right: 15px;
}
.icon__search {
  @apply text-blue-600;
}
.bwise-icon__workflow_approved {
  @apply text-green-600;
}
.bwise-icon__workflow_declined {
  @apply text-red-600;
}
</style>
